import { useEffect, useState } from "react";
import { Button } from "@/modern/components/ui/button-shadcn";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem
} from "@/modern/components/ui/dropdown-menu";
import { Moon, Sun, Check } from "lucide-react";
import { defaultPresets, ThemeStyleProps } from "@/styles/themes";
import { hexToCSSHSL } from "@/react/components/entityManager/themeUtils";
// @ts-expect-error - userPreferences is dynamically typed in JSX context
import { getSetting, userPreferences } from "@/react/context/bryntumContext";
import { setTheme as setBryntumTheme } from "@/bryntum-lib/widget/fields/ThemePicker";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { KeyValueModel } from "@/bryntum-lib/models/KeyValueModel";

const THEME_PRESETS_ENTRIES = Object.entries(defaultPresets);

// Load initial theme from localStorage and mode from user preferences
const getInitialTheme = () => {
  const savedTheme = localStorage.getItem("app-theme") || "default";

  // Load mode from userPreferences using "theme" key (same as SettingsBoardTab)
  // Theme ID "2" = Classic Light, Theme ID "3" = Classic Dark
  const themePref = getSetting("theme");
  const savedMode: "light" | "dark" = themePref === "3" ? "dark" : "light";

  return { theme: savedTheme, mode: savedMode };
};

// Build CSS rules for light or dark mode
const buildCssRules = (styles: Partial<ThemeStyleProps>) => {
  const rules: string[] = [];

  Object.entries(styles).forEach(([key, value]) => {
    // Skip undefined values
    if (value === undefined) return;

    // Handle radius directly
    if (key === "radius") {
      rules.push(`    --radius: ${value} !important;`);
      return;
    }

    // Skip font-*, shadow-*, letter-spacing, spacing properties
    if (key.startsWith("font-") || key.startsWith("shadow-") || key === "letter-spacing" || key === "spacing") {
      return;
    }

    // Handle special case: 'sidebar' maps to 'sidebar-background'
    const cssVarName = key === "sidebar" ? "sidebar-background" : key;

    // Convert hex to HSL for color variables
    const hslValue = hexToCSSHSL(value);
    rules.push(`    --${cssVarName}: ${hslValue} !important;`);
  });

  return rules.join("\n");
};

// Apply theme by injecting CSS variables into document head
const applyTheme = (themeKey: string) => {
  const theme = defaultPresets[themeKey];
  if (!theme) {
    console.error(`Theme not found: ${themeKey}`);
    return;
  }

  const lightStyles = theme.styles.light;
  const darkStyles = theme.styles.dark;

  const lightRules = buildCssRules(lightStyles);
  const darkRules = buildCssRules(darkStyles);

  // Create or update the style tag for dynamic theme
  let styleTag = document.getElementById("dynamic-theme-styles") as HTMLStyleElement;
  if (!styleTag) {
    styleTag = document.createElement("style");
    styleTag.id = "dynamic-theme-styles";
    document.head.appendChild(styleTag);
  }

  // Generate CSS with proper selectors
  styleTag.textContent = `
  .entity-page, [id^="radix-"], .b-chrome > div:has(.dashboard-theme) {
${lightRules}
  }

  [data-theme="dark"] .b-chrome > div:has(.dashboard-theme), [data-theme="dark"] .entity-page, [data-theme="dark"] [id^="radix-"] {
${darkRules}
  }
  `;
};

export function ThemeSwitcher() {
  const [theme, setTheme] = useState("default");
  const [mode, setMode] = useState<"light" | "dark">("dark");
  useSwivuStore<KeyValueModel>({
    storeId: STORE_KEYS.userPreferencesStore,
    subscriberFn: ({ record }) => {
      if (record.key === "theme") {
        const newMode = record.value === "3" ? "dark" : "light";
        setMode((prevState) => (prevState !== newMode ? newMode : prevState));
      }
    }
  });

  useEffect(() => {
    const initialTheme = getInitialTheme();
    handleThemeChange(initialTheme.theme);
    handleModeChange(initialTheme.mode, false);
  }, []);

  const handleThemeChange = (newTheme: string) => {
    if (newTheme in defaultPresets && newTheme !== theme) {
      setTheme(newTheme);
      localStorage.setItem("app-theme", newTheme);
      applyTheme(newTheme);
    }
  };

  const handleModeChange = (newMode: "light" | "dark", canApply: boolean = true) => {
    setMode(newMode);
    if (canApply) {
      const themeId = newMode === "dark" ? "3" : "2";
      setBryntumTheme(themeId);
      // @ts-expect-error - userPreferences is dynamically typed
      userPreferences.updateSetting("theme", themeId);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="xs" className="px-4 ">
          {defaultPresets[theme]?.label || "Default"}
          {mode === "light" ? <Sun /> : <Moon />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[200px] max-h-[500px]">
        <DropdownMenuLabel>Appearance</DropdownMenuLabel>
        <DropdownMenuSeparator />

        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">Mode</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={mode} onValueChange={(value) => handleModeChange(value as "light" | "dark")}>
          <DropdownMenuRadioItem value="light">
            <Sun className="h-4 w-4 mr-2" />
            Light
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="dark">
            <Moon className="h-4 w-4 mr-2" />
            Dark
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>

        <DropdownMenuSeparator />

        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">Theme</DropdownMenuLabel>
        {THEME_PRESETS_ENTRIES.map(([key, preset]) => {
          const styles = preset.styles[mode];
          const isSelected = theme === key;

          return (
            <DropdownMenuItem key={key} onClick={() => handleThemeChange(key)} className="cursor-pointer">
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-2">
                  <div className="flex gap-0.5">
                    <div className="w-3 h-3 rounded-sm border border-border/50" style={{ backgroundColor: styles.primary }} />
                    <div className="w-3 h-3 rounded-sm border border-border/50" style={{ backgroundColor: styles.secondary }} />
                    <div className="w-3 h-3 rounded-sm border border-border/50" style={{ backgroundColor: styles.accent }} />
                  </div>
                  <span>{preset.label || key}</span>
                </div>
                {isSelected && <Check className="h-4 w-4" />}
              </div>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
